import { useState } from 'react';
import { Stack } from '@mui/material';
import { yupResolver } from '@hookform/resolvers/yup';
import {
  Controller,
  FormProvider,
  useForm,
  useFormContext
} from 'react-hook-form';
import { v4 as uuid } from 'uuid';
import * as yup from 'yup';
import Button from 'components/Button';
import Text from 'components/Text';
import TextField from 'components/TextField';
import { BlockTypes, ICustomFieldBlock } from 'types';
import { CustomFieldInputTypesProps } from '../EditCustomFields';

const customFieldInternalMetadataInputSchema = yup.object({
  value: yup
    .string()
    .required('Value is required')
    .max(800, 'Value must be 800 characters or less')
});

const CustomFieldInternalMetadataInput = ({
  customField,
  onUpdate,
  name,
  legacyFieldMapping,
  nameError
}: CustomFieldInputTypesProps): JSX.Element => {
  const { control, formState } = useFormContext();
  const [value, setValue] = useState<string>(
    customField?.fieldInfo?.value || ''
  );

  const isFormUpdated =
    value !== customField?.fieldInfo?.value ||
    name !== customField?.name ||
    legacyFieldMapping !== customField?.legacyFieldMapping ||
    customField?.fieldInfo?.type !== 'internalMetadata';

  const updateInternalMetadataInput = () => {
    const updatedBlock: ICustomFieldBlock = {
      name,
      reportingKey: name,
      heading: name, // Use name as heading for internal fields
      id: customField?.id || uuid(),
      blockType: BlockTypes.CustomFieldBlock,
      fieldInfo: {
        type: 'internalMetadata',
        value,
        required: false // Internal metadata is never required
      },
      legacyFieldMapping
    };
    onUpdate(updatedBlock);
  };

  return (
    <Stack spacing={1}>
      <Text variant="h5">Value</Text>
      <Controller
        name="value"
        control={control}
        defaultValue={value || ''}
        render={({
          field: { ref, onChange, ...field },
          fieldState: { error }
        }) => (
          <TextField
            {...field}
            hiddenLabel
            placeholder="Metadata Value Here"
            onChange={(e) => {
              onChange(e.target.value);
              setValue(e.target.value as string);
            }}
            error={!!error}
            helperText={error?.message}
            inputProps={{ maxLength: 800 }}
          />
        )}
      />
      <Text variant="caption" color="textSecondary">
        Hidden to donors
      </Text>
      <Button
        variant="primary"
        fullWidth
        disabled={nameError || !formState.isValid || !isFormUpdated}
        onClick={updateInternalMetadataInput}
      >
        Update
      </Button>
    </Stack>
  );
};

export default (props: CustomFieldInputTypesProps): JSX.Element => {
  const methods = useForm({
    resolver: yupResolver(customFieldInternalMetadataInputSchema),
    mode: 'onChange'
  });

  return (
    <FormProvider {...methods}>
      <CustomFieldInternalMetadataInput {...props} />
    </FormProvider>
  );
};
